/**
 * "Your plan, in plain words" — ONE reconciliation for the WHOLE manual-control plan
 * (owner direction, 2026-08-24). Each strategy section keeps its own numbers (CGT boxes, the
 * "Where every dollar goes" ledger); this card is the INTERPRETATION LAYER — sentences for people
 * who don't understand the numbers above, never a restatement of them. An earlier cut rendered the
 * money as a second ledger and the owner rightly called it a duplicate: if a reader wanted the
 * table, it is right there above. What only this card gives them is the story ("the room is paid
 * in full, so no DAP is charged on it; the pension doesn't move because both destinations are
 * exempt") and the before→after movements with their whys.
 *
 * PRESENTATION: the app's own design language (gradient heading, ImpactMetric surface — see
 * PlainWords.tsx). Pension and cashflow are ShiftRows in ImpactMetric's exact before→after
 * grammar with a tappable "(why?)" naming this plan's ACTUAL drivers.
 *
 * CALCULATION DISCIPLINE — every figure is quoted from a source already on screen, never re-derived:
 *   • Property CGT / net proceeds: the SAME shared functions as the allocator's CGT box, with the
 *     same `details` (including the accountant override).
 *   • Share/bank funding: `computeRadFundingTotals` — the SAME function the analyser's `radPaid`
 *     uses, so the sentences quote exactly what the engine was given.
 *   • DAP: `agedCareCosts.dailyAccommodationPayment` — the rail's own figure.
 *   • "with this plan" pension & cashflow: `simulatedFinancials`, with care costs netted off using
 *     the IDENTICAL arithmetic as FinancialImpactPanel (cashFlow − totalDailyCost×365).
 *   • "with no plan": the shared `useWalkInBaseline` hook — the same computation the walk-in note
 *     at the top of the page quotes.
 * The walk-in-note frame bug (owner caught two frames in one sentence, 2026-08-24) is why this
 * discipline exists. Do not add a number here that is not one of these sources.
 */
import { recapPhaseLine } from './carePhases.ts';
import { fundingPhrase, type FundingLine } from './fundingLabels.ts';
import { shortfallOutlook, careYearsFor, type RunwayFacts } from './shortfallOutlook.ts';
import React from 'react';
import { capitalReturnedWords } from '../../components/strategy/annuityWords.ts';
import {
    pensionClause,
    cashflowClause,
    estateClause,
    plainClause,
    joinClauses,
    type RecapClause,
} from '../../components/strategy/recapClauses.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { AnnuityTerms, EngineAnnuityTerms } from '../../components/strategy/AnnuityTerms.tsx';
import { MPIR_PERCENT } from '../../constants/awiseKnowledgeBase.ts';
import { EquityReleaseTerms } from '../../components/strategy/EquityReleaseTerms.tsx';
import { JargonTerm } from '../../components/narrative/index.tsx';
import { PlainWordsCard, ShiftRow, WhatMovesIt, type EstateDriver } from '../../components/narrative/PlainWords.tsx';
import { PlanPiecesCard, type PlanPiece } from '../../components/narrative/PlanPieces.tsx';
import { useWalkInBaseline } from './WalkInPositionNote.tsx';
import {
    calculateInvestmentPropertyDisposalTax,
    getEffectiveDisposalCgt,
} from '../../../../shared/src/calculation/entityDisposalTax.ts';
import {
    computeRadFundingTotals,
    parseRadFundingAssetKey,
    findAssetInFactFind,
} from '../../utils/entityFundingUtils.ts';
import { GIFTING_LIMIT_ANNUAL } from '../../constants/index.ts';
import type { InvestmentPropertySaleDetails } from '../../utils/entityInvestmentPropertySale.ts';
import type { DownsizeDetails } from './DownsizeStrategyAllocator.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFind, FactFindAsset } from '../../../../shared/src/calculation/factFindTypes.ts';

/**
 * The one-or-two-sentence version, for the plan summary panel's "In brief".
 *
 * Deliberately takes FINISHED figures rather than computing anything: every argument is a number
 * already on screen in the panel's own tiles, so the sentence can never disagree with the tiles it
 * sits above. That is the whole discipline of this file (see the header) applied to its shortest
 * output — and it is why this is a pure function and not a second pass over the fact-find.
 *
 * Deltas are plan-minus-baseline, the same subtraction the tiles display. Pass null for anything the
 * caller could not compute; the clause is then simply omitted rather than guessed at.
 */
/*
 * The clause vocabulary now lives in components/strategy/recapClauses.tsx, shared by every module.
 *
 * It started here because Aged Care is the benchmark for this app's UI — and then stayed here,
 * which meant the bolding, the colour and the "still short by" clarification landed in Aged Care
 * only (owner-caught, 2026-08-31). Re-exported so this file's existing callers are untouched.
 */
export type { RecapClause } from '../../components/strategy/recapClauses.tsx';
export { RecapClauses } from '../../components/strategy/recapClauses.tsx';

export interface PlanRecapInput {
    roomFullyPaid: boolean;
    /** Annual DAP still payable — quoted only when the room is NOT fully paid. */
    dapAnnual: number;
    pensionDelta: number | null;
    cashflowDelta: number | null;
    legacyDelta: number | null;
    legacyYears: number;
    /**
     * The plan's ABSOLUTE annual cashflow, where known.
     *
     * OWNER-CAUGHT, 2026-08-31: "cashflow is better by $20,517" reads as a surplus when the plan is
     * still $51,229 in deficit. An improvement stated without its level is the most flattering true
     * sentence available, and a client acting on it would badly misjudge what they can afford.
     */
    cashflowAfter?: number | null;
    /**
     * The plan's FULL annual care cost — basic daily fee, means-tested contribution, hotelling,
     * extra services and the DAP together. Owner, 22 Sep 2026: quoting the DAP alone ("$28,662 a
     * year is still charged…") beside a Total Aged Care Costs tile of $60,391 confused a reader at
     * once. With this known, the clause states the whole and places the DAP inside it.
     */
    careAnnual?: number | null;
}

export function buildPlanRecapClauses(input: PlanRecapInput): RecapClause[] {
    const clauses: RecapClause[] = [
        /*
         * NOT "nothing is charged daily" (owner-caught, 2026-08-26). Paying the room in full stops
         * the DAP and nothing else: the basic daily fee, the care contribution and the hotelling
         * supplement all continue, and on the very screen this sentence appears the Cost Overview
         * was still showing $196 a day. Naming the one fee that stops is the whole point.
         */
        roomClause(input),
    ];
    if (input.pensionDelta != null) clauses.push(pensionClause(input.pensionDelta));
    if (input.cashflowDelta != null && Math.round(input.cashflowDelta) !== 0) {
        clauses.push(cashflowClause(input.cashflowDelta, input.cashflowAfter));
    }
    if (input.legacyDelta != null) clauses.push(estateClause(input.legacyDelta, input.legacyYears));
    return clauses;
}

/** The room clause: the year's care cost as a whole, and the DAP's share of it, both in bold. */
function roomClause(input: PlanRecapInput): RecapClause {
    const care = input.careAnnual != null && input.careAnnual > 0 ? formatCurrency(Math.round(input.careAnnual)) : null;
    const dap = formatCurrency(Math.round(input.dapAnnual));
    if (input.roomFullyPaid) {
        return care
            ? plainClause(`the room is paid in full, so the daily accommodation payment stops — the other care fees continue at ${care} a year`, [care])
            : plainClause('the room is paid in full, so the daily accommodation payment stops — the other care fees continue');
    }
    return care
        ? plainClause(`care costs ${care} a year, of which ${dap} is the DAP charged on the unpaid part of the room`, [care, dap])
        : plainClause(`${dap} a year is still charged for the unpaid part of the room as a DAP`, [dap]);
}

/**
 * The same In Brief as one plain sentence.
 *
 * Kept because several surfaces want a string — the SOA, the saved-strategy record, anything that
 * cannot render markup. It is DERIVED from the clauses rather than built separately, so the sentence
 * and the panel can never say different things.
 */
export function buildPlanRecapHeadline(input: PlanRecapInput): string {
    return joinClauses(buildPlanRecapClauses(input)) || '';
}

/** Direction words and derived amounts, separated from React so the rules are unit-testable. */
export interface PlanRecapFigures {
    unpaidRoom: number;
    dapAnnual: number;
    roomFullyPaid: boolean;
    pensionDirection: 'rises' | 'falls' | 'holds';
    cashflowDirection: 'improves' | 'worsens' | 'holds';
}

export function buildPlanRecapFigures(input: {
    radInput: number;
    /** TOTAL paid up front from every source — it is what sets the DAP. */
    radPaidTotal: number;
    dapDaily: number;
    pensionDoNothing: number;
    pensionWithPlan: number;
    cashflowDoNothing: number;
    cashflowWithPlan: number;
}): PlanRecapFigures {
    const unpaidRoom = Math.max(0, input.radInput - input.radPaidTotal);
    // Directions compare ROUNDED dollars: a 40-cent drift between two engine runs must read as
    // "holds", never as a rise or fall the client then asks their adviser to explain.
    const moved = (before: number, after: number) => Math.round(after) - Math.round(before);
    const pension = moved(input.pensionDoNothing, input.pensionWithPlan);
    const cashflow = moved(input.cashflowDoNothing, input.cashflowWithPlan);
    return {
        unpaidRoom,
        dapAnnual: input.dapDaily * 365,
        roomFullyPaid: unpaidRoom < 1,
        pensionDirection: pension > 0 ? 'rises' : pension < 0 ? 'falls' : 'holds',
        cashflowDirection: cashflow > 0 ? 'improves' : cashflow < 0 ? 'worsens' : 'holds',
    };
}

const ASSET_TYPE_FALLBACKS: Record<string, string> = {
    bankAccounts: 'bank savings',
    termDeposits: 'a term deposit',
    shares: 'shares',
    accountBasedPensions: 'an account-based pension',
    superannuation: 'super',
};

interface StrategyPlanRecapProps {
    factFind: DraftFactFind;
    overallStrategy: { sellInvestmentProperty?: boolean; downsizeHome?: boolean; reverseMortgage?: boolean; rentHome?: boolean; heas?: boolean };
    /**
     * The rent-the-family-home assumptions. The summary was SILENT on this whole strategy
     * (owner-caught, 2026-08-26): a plan that let the home out at $2,000 a week and had the partner
     * paying $600 a week in rent elsewhere described neither, and then explained the pension fall as
     * though assets had been sold — when nothing had been sold and the real driver was the rent
     * landing in the income test.
     */
    strategyInputs?: { rentAmount?: number; rentExpenses?: number; partnerRent?: number };
    /**
     * The analyser's shared no-plan baseline. When present its figures WIN over this component's
     * own hook, so the summary and the Financial Impact panel are quoting one computation rather
     * than two that can drift apart. See the note where these are read.
     */
    sharedBaseline?: { pension: number | null; cashFlowAfterCare: number | null } | null;
    /** How long this plan's savings last and how far its projection reaches — for the shortfall reassurance (shortfallOutlook.ts). */
    planRunway?: RunwayFacts | null;
    /** Where the shortfall comes from, in order, ending on the deposit's refund (moneyOrderParagraph). Shown only beside a shortfall. */
    moneyOrder?: string | null;
    /** Projected estate with NO plan — the benchmark. Null when it could not be computed. */
    baselineLegacy?: number | null;
    /** Projected estate with this plan. */
    planLegacy?: number | null;
    legacyYears?: number;
    /** Whether the Home Equity Access Scheme is on, and what it draws. */
    heasDetails?: { lumpSum?: number | string; fortnightlyPayment?: number | string; interestRate?: number | string } | null;
    /** Debts this plan clears, so the summary can say what the money bought. */
    debtPayoffPlan?: { payments?: Record<string, number> } | null;
    /** Standalone gifting / funeral bond / annuity — chosen outside a property sale. */
    otherStrategies?: { gifting?: boolean; funeralBonds?: boolean; annuity?: boolean } | null;
    /**
     * The annuity terms reported by the SUGGESTION ENGINE, where the plan came from there.
     *
     * Manual Control keeps its terms on `annuityRecord`; an engine-built annuity is applied straight
     * into the fact-find and has no such record, so the engine reports them separately. Either route
     * ends up rendering the same component - the disclosure must not depend on which screen built
     * the plan (owner, 2026-09-01).
     */
    engineAnnuityTerms?: Record<string, unknown> | null;
    /**
     * The equity-release loan this strategy takes, where it takes one, with what the projection says
     * it grows to.
     *
     * Owner-raised, 2026-09-01: a reverse mortgage strategy was leading the cashflow ranking and no
     * screen said the product's name, let alone what it costs. There are no repayments, so the cost
     * is invisible in every cashflow figure — it is entirely in the closing balance.
     */
    equityRelease?: Record<string, unknown> | null;
    /**
     * A control for entering a real provider quote, supplied by the module.
     *
     * The recap does not own it because only the panel can re-rank the field on the new number -
     * and a quote that relabelled one card without re-ranking would be worse than none.
     */
    annuityQuoteControl?: React.ReactNode;
    annuityRecord?: {
        purchaseAmount?: number | string;
        annualPayment?: number;
        /*
         * The TERMS the annuity was built on. Already carried by every annuity record
         * (shared/src/calculation/annuityStrategies.ts returns them); simply never read here, so
         * the recap described a $500,000 commitment without saying who owned it or whether the
         * survivor kept the income. Owner-raised, 2026-09-01.
         */
        residualCapital?: number;
        owner?: string;
        reversionary?: boolean;
        indexation?: boolean;
        annuityType?: string;
    } | null;
    investmentPropertyDetails: InvestmentPropertySaleDetails;
    downsizeDetails: DownsizeDetails;
    reverseMortgageDetails: { useForRad?: boolean; loanStructure?: string; loanAmount?: number | string };
    radFunding: Record<string, number>;
    cgtOverrides: Record<string, unknown>;
    radInput: number;
    /** The second room, where both of a couple enter care: its price, deposit and daily cost. */
    secondRoom?: { name: string; leadName?: string; roomPrice: number; radPaid: number; dailyTotal: number | null; funding?: FundingLine[]; paidFromLabel?: string } | null;
    /** The second stage, where the second person enters later: both rooms from then (carePhases.ts). */
    phaseTwo?: { names: { firstName: string; secondName: string; fromLabel: string }; firstDaily: number; phaseTwoDaily: number } | null;
    /** A change of home (facilityMoveWords.movePiece): the paragraph that opens the story. */
    facilityMove?: string | null;
    /** Leaving a retirement village (villageExitWords.villagePiece): the paragraph that opens the story. */
    villageExit?: string | null;
    /** Total RAD paid from all funding sources (the analyser's `radPaid`). */
    radPaid: number;
    agedCareCosts: { totalDailyCost?: number; dailyAccommodationPayment?: number };
    simulatedFinancials: { socialSecurity?: number; cashFlow?: number };
    extraServiceFeeInput?: number;
    /** Annuity purchase funded from the sale proceeds pool (analyser subtracts it from the bank leftover). */
    annuityFromSaleProceeds?: number;
    /**
     * The card's heading. The DEFAULT is the heading agreed across all five modules' recaps
     * (rvFundingCommentary.test.ts) and must stay that way.
     *
     * Pass null only where the surrounding surface already names the card. Aged Care does: this
     * recap now opens inside "Understanding your plan", with the plan summary panel headed "Your
     * plan summary" right beside it, so the default would put the same words on screen three times.
     */
    eyebrow?: string | null;
}

const StrategyPlanRecap = ({
    factFind,
    overallStrategy,
    investmentPropertyDetails,
    downsizeDetails,
    reverseMortgageDetails,
    radFunding,
    cgtOverrides,
    radInput,
    radPaid,
    agedCareCosts,
    simulatedFinancials,
    extraServiceFeeInput = 0,
    annuityFromSaleProceeds = 0,
    eyebrow = 'Your plan summary',
    strategyInputs,
    sharedBaseline = null,
    planRunway = null,
    moneyOrder = null,
    secondRoom = null,
    phaseTwo = null,
    facilityMove = null,
    villageExit = null,
    baselineLegacy = null,
    planLegacy = null,
    legacyYears = 5,
    heasDetails = null,
    debtPayoffPlan = null,
    otherStrategies = null,
    annuityRecord = null,
    engineAnnuityTerms = null,
    equityRelease = null,
    annuityQuoteControl = null,
}: StrategyPlanRecapProps) => {
    const baseline = useWalkInBaseline(factFind, radInput, extraServiceFeeInput);

    /*
     * RENTING THE FAMILY HOME — a whole strategy the summary used to omit.
     *
     * It matters twice over: the net rent is assessable income for BOTH the Age Pension income test
     * and the aged care means test, and the home keeps its two-year assets-test exemption for the
     * pension while being counted (to the cap) for care fees. A summary that skips it cannot explain
     * its own pension figure — which is exactly how a correct result came to carry a wrong reason.
     */
    const clearedDebtsForWhy = Object.values(debtPayoffPlan?.payments || {}).reduce((sum, amount) => sum + (Number(amount) || 0), 0);
    const rentsHome = overallStrategy?.rentHome === true;
    const weeklyRent = Math.max(0, Number(strategyInputs?.rentAmount) || 0);
    const annualRent = weeklyRent * 52;
    const rentExpenses = Math.max(0, Number(strategyInputs?.rentExpenses) || 0);
    const netAnnualRent = Math.max(0, annualRent - rentExpenses);
    const partnerWeeklyRent = Math.max(0, Number(strategyInputs?.partnerRent) || 0);
    const partnerAnnualRent = partnerWeeklyRent * 52;

    // ---- Investment property sale (same resolution + shared functions as the allocator's box) ----
    const ipActive = !!overallStrategy.sellInvestmentProperty;
    const selectedProperty = ipActive
        ? factFind.assets?.investmentProperty?.find((p) => p.id === investmentPropertyDetails.selectedPropertyId) ||
          factFind.assets?.investmentProperty?.[0]
        : undefined;
    const rawTaxSummary = selectedProperty
        ? calculateInvestmentPropertyDisposalTax(
              factFind as unknown as FactFind,
              selectedProperty as unknown as FactFindAsset,
              investmentPropertyDetails,
          )
        : null;
    const ipTax = getEffectiveDisposalCgt(rawTaxSummary, investmentPropertyDetails.cgtOverrideTotal);
    const ipNetProceeds = ipTax?.netProceeds ?? 0;
    const ipCgt = ipTax?.estimatedCGT ?? 0;
    const ip = {
        toRoom: ipActive ? Number(investmentPropertyDetails.radAllocation) || 0 : 0,
        newHome: ipActive ? Number(investmentPropertyDetails.newHomePurchasePrice) || 0 : 0,
        downsizer: ipActive ? Number(investmentPropertyDetails.downsizerContribution) || 0 : 0,
        gifting: ipActive ? Number(investmentPropertyDetails.giftingAmount) || 0 : 0,
        funeralBond: ipActive ? Number(investmentPropertyDetails.funeralBondPurchase) || 0 : 0,
    };
    // Same allocation list as the IP allocator's own unallocated figure.
    const ipLeftover = ipActive
        ? Math.max(0, ipNetProceeds - (ip.toRoom + ip.newHome + ip.downsizer + ip.gifting + ip.funeralBond))
        : 0;

    // ---- Liquid-asset funding ("Paying for the room") — same function as the analyser's radPaid ----
    const funding = computeRadFundingTotals(factFind, radFunding, cgtOverrides);
    const liquidRows = Object.entries(funding.byKey)
        .filter(([, entry]) => entry.gross > 0)
        .map(([key, entry]) => {
            const parsed = parseRadFundingAssetKey(key);
            const located = parsed ? findAssetInFactFind(factFind, parsed.assetType, parsed.assetId) : null;
            const label =
                located?.asset?.description ||
                (parsed ? ASSET_TYPE_FALLBACKS[parsed.assetType] : null) ||
                'savings';
            return { key, label, ...entry };
        });

    /*
     * The downsize, told IN FULL — every destination, not just the room money. An earlier cut showed
     * only the RAD; the owner ran a plan whose visible destinations (RAD, new home) were both exempt
     * while the leftover proceeds sat in the bank being deemed, and a correct engine result read as
     * a bug. A pension movement can only be explained by naming the money that stays COUNTABLE. The
     * field list mirrors the analyser's simulatedFactFind downsize branch exactly.
     */
    const dz = {
        salePrice: parseFloat(String(downsizeDetails.salePrice)) || 0,
        sellingCosts: parseFloat(String(downsizeDetails.sellingCosts)) || 0,
        toRoom: parseFloat(String(downsizeDetails.radAllocation)) || 0,
        newHome: parseFloat(String(downsizeDetails.newHomePurchasePrice)) || 0,
        downsizer:
            (parseFloat(String(downsizeDetails.downsizerContribution)) || 0) +
            (parseFloat(String(downsizeDetails.partnerDownsizerContribution)) || 0),
        gifting: parseFloat(String(downsizeDetails.giftingAmount)) || 0,
        funeralBond: parseFloat(String(downsizeDetails.funeralBondPurchase)) || 0,
        newInvestmentProperty: parseFloat(String(downsizeDetails.purchaseInvestmentProperty)) || 0,
        newShares: parseFloat(String(downsizeDetails.purchaseShares)) || 0,
    };
    const dzActive = !!overallStrategy.downsizeHome;
    const dzNetProceeds = dzActive ? Math.max(0, dz.salePrice - dz.sellingCosts) : 0;
    const dzAllocated =
        dz.toRoom + dz.newHome + dz.downsizer + dz.gifting + dz.funeralBond + dz.newInvestmentProperty + dz.newShares;
    // The analyser routes an annuity bought "from sale proceeds" out of the leftover bank pool
    // before it lands — mirror that, or this leftover would overstate what stays deemed.
    const dzLeftover = dzActive ? Math.max(0, dzNetProceeds - dzAllocated - (annuityFromSaleProceeds || 0)) : 0;

    const stayingName =
        factFind.adviceFor === 'couple'
            ? ((factFind.goals as { aged_care_for?: string })?.aged_care_for === 'partner'
                  ? factFind.client?.firstName
                  : factFind.partner?.firstName) || null
            : null;

    /*
     * A LOADING STATE, because the blank was being read as broken (owner-reported, 2026-08-31:
     * "Understanding your plan is only showing the title and nothing else").
     *
     * `useWalkInBaseline` makes two API round-trips, and until BOTH land `baseline.costs` and
     * `baseline.careFinancials` are null. This returned null for the whole of that window — so the
     * explanation modal opened with a title and an empty white box, then filled in a beat later.
     * Nothing was wrong; it simply had nothing to say yet and did not say so.
     *
     * The two cases are separated: with no room price there is genuinely nothing to explain and
     * rendering nothing is right. Waiting for figures is different, and says so.
     */
    if (!(radInput > 0)) return null;
    if (!baseline.costs || !baseline.careFinancials) {
        return (
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-5">
                <p className="text-sm text-slate-500">Working out what this plan does…</p>
            </div>
        );
    }

    const rmToRoom =
        overallStrategy.reverseMortgage &&
        reverseMortgageDetails.useForRad &&
        reverseMortgageDetails.loanStructure === 'lump_sum'
            ? parseFloat(String(reverseMortgageDetails.loanAmount)) || 0
            : 0;

    const hasDownsizeGroup = dzActive && dzNetProceeds > 0;
    const hasIpGroup = ipActive && !!selectedProperty && ipNetProceeds > 0;
    const hasLiquidGroup = liquidRows.length > 0 || rmToRoom > 0;

    /*
     * A PLAN THAT PAYS NO DEPOSIT STILL HAS AN EXPLANATION, and this returned null for every one of
     * them.
     *
     * OWNER-CAUGHT, 2026-09-01, on "Pay the DAP + $500k lifetime annuity": "Explain this for the
     * annuity strategy is missing the rest of the breakdown that would otherwise be shown." The
     * modal opened with a title, the annuity box, and nothing else.
     *
     * The three groups above all ask WHERE THE DEPOSIT MONEY CAME FROM. Paying the daily fee instead
     * is a deliberate strategy - it keeps every dollar liquid and pays for the room like rent - so
     * there is no funding source to narrate, and the guard read that as "nothing to explain". It is
     * the opposite: the whole point of the plan is what the client KEPT, and the pension, cashflow
     * and estate rows below are exactly as meaningful. Only the funding paragraphs are missing, and
     * `story` simply has one fewer paragraph.
     *
     * The guard stays for the genuinely empty case: no deposit, no daily fee, no lever - nothing has
     * been decided yet.
     */
    const paysDailyFee = radInput > (Number(radPaid) || 0) + 1;
    const hasMeansTestLever =
        !!otherStrategies?.annuity || !!otherStrategies?.gifting || !!otherStrategies?.funeralBonds;
    // A deposit paid wholly from money already coming — a village's exit payment, a current room's
    // refund — sells nothing and pays no daily fee, and is still a plan to explain (owner-caught,
    // 9 Oct 2026: the modal opened empty on "Pay 100% RAD ($750,000, all from the village money)").
    const paidFromMoneyComing = !!villageExit || !!facilityMove;
    if (!hasDownsizeGroup && !hasIpGroup && !hasLiquidGroup && !paysDailyFee && !hasMeansTestLever && !paidFromMoneyComing) return null;

    // "With no plan" = the walk-in baseline (matches the note at the top of the page).
    // "With this plan" = the same arithmetic the Financial Impact panel uses (matches it exactly).
    /*
     * ONE BASELINE. These figures come from the analyser's shared `agedCareBaseline` whenever it has
     * resolved, and fall back to this component's own hook only if it has not.
     *
     * Aged care used to compute the no-plan position TWICE — once here (via useWalkInBaseline) and
     * once in the analyser for the Financial Impact panel. Same intent, two code paths, and they
     * disagreed the moment one changed: the panel showed a baseline cashflow of $24,414 while this
     * summary showed −$73,235 for the same plan on the same screen, because only one of them netted
     * off the baseline's own care fees. Two computations of one number will always drift; the fix is
     * to have one.
     */
    const pensionDoNothing = sharedBaseline?.pension ?? (baseline.careFinancials.socialSecurity || 0);
    const pensionWithPlan = simulatedFinancials.socialSecurity || 0;
    const cashflowDoNothing =
        sharedBaseline?.cashFlowAfterCare ??
        ((baseline.careFinancials.cashFlow || 0) - (baseline.costs.totalDailyCost || 0) * 365);
    const cashflowWithPlan =
        (simulatedFinancials.cashFlow || 0) - (agedCareCosts.totalDailyCost || 0) * 365;
    const dapDaily = agedCareCosts.dailyAccommodationPayment || 0;

    const figures = buildPlanRecapFigures({
        radInput,
        radPaidTotal: radPaid,
        dapDaily,
        pensionDoNothing,
        pensionWithPlan,
        cashflowDoNothing,
        cashflowWithPlan,
    });

    const totalGifting = ip.gifting + (dzActive ? dz.gifting : 0);
    const giftExcess = Math.max(0, totalGifting - GIFTING_LIMIT_ANNUAL);
    const totalLeftover = dzLeftover + ipLeftover;

    /*
     * The whys name this plan's ACTUAL drivers — the same figures the sections above show — so the
     * explanation and the numbers can never tell different stories. Generic cause-lists are what
     * made a correct result read as a bug (owner feedback, 2026-08-24 twice: the exempt-downsize
     * phantom fall, and "any rent stops" said to a client who sold no rented asset).
     */
    const countable: string[] = [];
    /*
     * Rent first, because when it is on it is usually the whole answer — and it was the case that
     * exposed this list as incomplete. A client who funded the RAD from the bank and let the home
     * out sold nothing at all, so every entry below stayed empty and the summary fell through to a
     * sentence about selling assets. The RAD is exempt; the rent is not; that is the story.
     */
    if (rentsHome && netAnnualRent > 0) {
        countable.push(
            `${formatCurrency(netAnnualRent)} a year of net rent from the family home (${formatCurrency(annualRent)} of rent less ${formatCurrency(rentExpenses)} of expenses), which the income test counts`,
        );
    }
    if (dzLeftover > 0) countable.push(`${formatCurrency(dzLeftover)} of home-sale money left in the bank`);
    if (ipLeftover > 0) countable.push(`${formatCurrency(ipLeftover)} of property-sale money left in the bank`);
    if (dzActive && dz.downsizer > 0) countable.push(`${formatCurrency(dz.downsizer)} of downsizer contributions in super (counted from pension age)`);
    if (dzActive && dz.newShares > 0) countable.push(`${formatCurrency(dz.newShares)} of new shares`);
    if (dzActive && dz.newInvestmentProperty > 0) countable.push(`the ${formatCurrency(dz.newInvestmentProperty)} new investment property`);

    const pensionWhy =
        figures.pensionDirection === 'rises'
            ? `Centrelink counted the assets being sold in full — their value in the assets test and any rent or earnings in the income test. Money paid as a RAD is exempt from the assets test, so moving value into the RAD usually lifts the pension. The Financial Impact panel shows the same before-and-after.`
            : figures.pensionDirection === 'falls'
              ? countable.length > 0
                  ? `The RAD and any new family home are exempt — what moves the pension is what stays countable: ${countable.join('; ')}.${
                        rentsHome && netAnnualRent > 0
                            ? ' Note the money used to pay the RAD is not what caused this: a RAD is exempt from the assets test, so paying it does not reduce the pension. It is the rent that does.'
                            : ' Centrelink counts it in the assets test and deems income on the financial part.'
                    } The Financial Impact panel shows the same before-and-after.`
                  : `Nothing in this plan obviously moves what the means tests count, yet the pension has changed — check the Age Pension breakdown in the Financial Impact panel for the line that moved, rather than relying on this summary. The Financial Impact panel shows the same before-and-after.`
              : `Both sides of this plan sit in things Centrelink treats the same way as before, so nothing here changes what the means tests count. The Financial Impact panel shows the same before-and-after.`;

    /*
     * WHY THE ESTATE MOVED — named, quantified, and in a lay reader's terms.
     *
     * Owner-caught (2026-08-26): a plan that sold the home, paid the whole room and bought a cheaper
     * house showed the estate $163,528 WORSE than doing nothing, and the explanation said nothing
     * useful. The adviser's own reaction — "I'm not clear on this because the DAP is the higher
     * interest rate" — is exactly right and exactly the point: paying the room up front DOES save the
     * daily fee, so the reader is owed the other side of the ledger.
     *
     * The two costs that are easy to miss, because neither appears as a fee:
     *   1. SELLING COSTS leave immediately and never return.
     *   2. A RAD DOES NOT GROW. It is refunded at the amount paid — the engine excludes `radPaid`
     *      from asset growth for exactly this reason. Money moved out of a home (which rises with
     *      property values) into a deposit that stands still costs the estate that forgone growth,
     *      every year it sits there. Nothing on the screen says "fee", so nobody sees it.
     * Both are quoted from figures already on this page; the netting off is the projection's job.
     */
    const sellingCostsPaid = dzActive ? Math.max(0, Number(downsizeDetails.sellingCosts) || 0) : 0;
    const parkedInRad = Math.min(radPaid, radInput);
    /**
     * Did the deposit come out of the HOME, or out of savings? It changes what growth was given up —
     * property growth in one case, investment return in the other — and saying the wrong one to a
     * client who never touched their home is worse than saying nothing.
     */
    const radFundedFromHomeSale =
        dzActive && (Number(downsizeDetails.radAllocation) || 0) > 0;
    const baselineDapAnnualForWhy = (baseline.costs.dailyAccommodationPayment || 0) * 365;
    /*
     * WHAT MOVES THE ESTATE, AS A LIST (owner, 24 Sep 2026: "aged care is still one massive block
     * of text"). One line per driver, the amount beside it where the model has one. The mechanism
     * of each is unchanged from the sentences this replaces — see the notes on the RAD line:
     * the provider retains 2% a year for the first five years (radPlugin.ts models exactly that),
     * and the growth given up is property growth only when the deposit came out of a home sale.
     */
    const legacyDrivers: EstateDriver[] = [];
    if (sellingCostsPaid > 0) legacyDrivers.push({ text: 'The selling cost leaves straight away and never comes back', amount: sellingCostsPaid, sign: 'minus' });
    if (parkedInRad > 0) {
        legacyDrivers.push({
            text: `The ${formatCurrency(parkedInRad)} paid as a RAD comes back when care ends, less the 2% a year the provider retains for the first five years, and it does NOT grow while it sits there — ${
                radFundedFromHomeSale ? 'money that was in a home rising with property values is now standing still' : 'money that was invested and earning is now standing still'
            }, so the estate gives up that growth for every year it sits there`,
            sign: 'minus',
        });
    }
    if (figures.roomFullyPaid && baselineDapAnnualForWhy > 0) {
        legacyDrivers.push({ text: 'Paying the room up front removes the daily fee, pulling the other way — which is why the cashflow above improves', amount: baselineDapAnnualForWhy, suffix: 'a year', sign: 'plus' });
    } else if (figures.unpaidRoom > 0) {
        const dapAnnualNow = dapDaily * 365;
        if (parkedInRad > 0 && baselineDapAnnualForWhy > dapAnnualNow + 0.5) {
            legacyDrivers.push({ text: `Paying ${formatCurrency(parkedInRad)} up front cuts the daily fee from ${formatCurrency(baselineDapAnnualForWhy)} to ${formatCurrency(dapAnnualNow)} a year, pulling the other way`, amount: baselineDapAnnualForWhy - dapAnnualNow, suffix: 'a year', sign: 'plus' });
        } else {
            legacyDrivers.push({ text: `The ${formatCurrency(figures.unpaidRoom)} of room left unpaid is charged as a daily fee and never comes back`, amount: dapAnnualNow, suffix: 'a year', sign: 'minus' });
        }
    }
    if (rentsHome && netAnnualRent > 0) legacyDrivers.push({ text: 'The home is kept, and keeps both its rent and its property growth', amount: netAnnualRent, suffix: 'a year', sign: 'plus' });
    if (totalGifting > 0) legacyDrivers.push({ text: 'Gifted, so it has left the estate altogether', amount: totalGifting, sign: 'minus' });
    if (clearedDebtsForWhy > 0) legacyDrivers.push({ text: 'Debt cleared is money out of the estate now, against interest that never accrues', amount: clearedDebtsForWhy, sign: 'neutral' });
    if (Math.max(0, Number(heasDetails?.lumpSum) || 0) > 0 || Math.max(0, Number(heasDetails?.fortnightlyPayment) || 0) > 0) {
        legacyDrivers.push({ text: 'The Home Equity Access Scheme loan compounds against the home and is repaid out of the estate', sign: 'minus' });
    }
    const legacyWhy = (
        <WhatMovesIt
            lead={`What is projected to be left after ${legacyYears} ${legacyYears === 1 ? 'year' : 'years'}, measured against doing nothing — paying the whole room as a daily fee and changing nothing else. The care is happening either way, so this isolates what the plan itself does to the estate.`}
            drivers={legacyDrivers}
            closing="Projections depend on the return, inflation, property-growth and length-of-stay assumptions behind them, so treat the direction as the message rather than the exact figure."
        />
    );

    const baselineDapAnnual = (baseline.costs.dailyAccommodationPayment || 0) * 365;
    const cashflowDrivers: string[] = [];
    // Rent moves cashflow in both directions at once, and a summary that names only one of them
    // misleads: the home earns, and the partner's new rent spends.
    if (rentsHome && netAnnualRent > 0) {
        cashflowDrivers.push(`the family home brings in ${formatCurrency(netAnnualRent)} a year of net rent`);
    }
    if (rentsHome && partnerAnnualRent > 0) {
        cashflowDrivers.push(`${factFind?.partner?.firstName || 'the partner'} pays ${formatCurrency(partnerAnnualRent)} a year in rent elsewhere`);
    }
    if (figures.roomFullyPaid && baselineDapAnnual > 0) {
        cashflowDrivers.push(
            `with no plan, ${formatCurrency(baselineDapAnnual)} a year of DAP would be charged on the unpaid room — this plan pays the room in full, so that charge disappears`,
        );
    } else if (baselineDapAnnual - figures.dapAnnual > 1) {
        cashflowDrivers.push(
            `the DAP shrinks from ${formatCurrency(baselineDapAnnual)} to ${formatCurrency(figures.dapAnnual)} a year because more of the room is paid up front`,
        );
    }
    if (figures.pensionDirection !== 'holds') {
        cashflowDrivers.push(
            `the Age Pension ${figures.pensionDirection === 'rises' ? 'is higher' : 'is lower'} (${formatCurrency(pensionDoNothing)} → ${formatCurrency(pensionWithPlan)} a year)`,
        );
    }
    if (hasIpGroup && (parseFloat(String(selectedProperty?.rent)) || 0) > 0) {
        cashflowDrivers.push(`the rent from ${selectedProperty?.description || 'the investment property'} stops with the sale`);
    }
    const cashflowWhy =
        `This is a full year of money in, minus living costs and care fees — the same figure the Financial Impact panel shows.` +
        (cashflowDrivers.length > 0 ? ` What changes it against doing nothing: ${cashflowDrivers.join('; ')}.` : '') +
        // A plan that still leaves a shortfall: normal in care, and fine if the savings last (owner, 6 Oct 2026).
        (cashflowWithPlan != null && cashflowWithPlan < 0
            ? (() => { const o = shortfallOutlook(planRunway, careYearsFor(factFind)); return ` ${o.lead}${o.verdict ? ` ${o.verdict}` : ''}`; })()
            : '');

    // ---- The story: sentences that INTERPRET the numbers above, never restate them as a table ----
    const liquidPhrase = liquidRows
        .map((row) => `${formatCurrency(row.net)} from ${row.label}${row.cgt > 0 ? ' (after estimated capital gains tax)' : ''}`)
        .join(' and ');

    const story: React.ReactNode[] = [];
    if (hasDownsizeGroup) {
        story.push(
            <p key="dz">
                Selling the family home frees up {formatCurrency(dzNetProceeds)} after costs.
                {dz.toRoom > 0 && (
                    <> {formatCurrency(dz.toRoom)} of it goes to the room as a <JargonTerm term="RAD" /> — money that
                    comes back later and that Centrelink doesn't count.</>
                )}
                {dz.newHome > 0 && (
                    <> {formatCurrency(dz.newHome)} buys the new home{stayingName ? <> for {stayingName}</> : null} —
                    also not counted.</>
                )}
                {dz.downsizer > 0 && (
                    <> {formatCurrency(dz.downsizer)} goes into super as a <JargonTerm term="downsizer contribution" /> —
                    Centrelink does count this at pension age.</>
                )}
                {dz.newShares > 0 && (
                    <> {formatCurrency(dz.newShares)} buys shares, which Centrelink counts and <JargonTerm term="deems" >
                    {'Centrelink assumes financial assets earn a set rate of income (deeming) and counts that income in the pension income test, whatever they actually earn.'}
                    </JargonTerm> income on.</>
                )}
                {dz.newInvestmentProperty > 0 && (
                    <> {formatCurrency(dz.newInvestmentProperty)} buys an investment property, which is fully counted.</>
                )}
                {dz.funeralBond > 0 && (
                    <> {formatCurrency(dz.funeralBond)} goes into a funeral bond (exempt within the limit).</>
                )}
                {dz.gifting > 0 && <> {formatCurrency(dz.gifting)} is gifted.</>}
                {annuityFromSaleProceeds > 0 && (
                    <> {formatCurrency(annuityFromSaleProceeds)} buys the lifetime annuity set up under Other Strategies.</>
                )}
            </p>,
        );
    }
    if (hasIpGroup) {
        story.push(
            <p key="ip">
                Selling {selectedProperty?.description || 'the investment property'} frees up {formatCurrency(ipNetProceeds)}
                {ipCgt > 0 ? (
                    <> after selling costs and an estimated {formatCurrency(ipCgt)} in <JargonTerm term="capital gains tax" /></>
                ) : (
                    <> after selling costs, with no capital gains tax expected</>
                )}
                {ip.toRoom > 0 && <>; {formatCurrency(ip.toRoom)} of it goes to the room</>}
                {ip.newHome > 0 && <>, {formatCurrency(ip.newHome)} buys a new home</>}
                {ip.gifting > 0 && <>, {formatCurrency(ip.gifting)} is gifted</>}.
            </p>,
        );
    }
    /*
     * THE LOAN, TOLD FIRST AND IN FULL.
     *
     * The story previously carried one clause — "borrowed against the home ... the interest builds up
     * until the home is sold" — buried mid-paragraph among the funding sentences. For an $800,000
     * loan compounding at 8% with no repayments, that is not enough, and the owner reasonably read
     * the whole screen without realising which product they were looking at.
     */
    if (equityRelease && Number(equityRelease.loanAmount) > 0) {
        story.push(
            <EquityReleaseTerms
                key="equity-release"
                kind={equityRelease.kind === 'heas' ? 'heas' : 'reverseMortgage'}
                loanAmount={Number(equityRelease.loanAmount) || 0}
                interestRate={Number(equityRelease.interestRate) || null}
                projectedBalance={Number(equityRelease.projectedBalance) || null}
                years={Number(equityRelease.years) || null}
                homeValue={Number(factFind?.home?.value) || null}
            />,
        );
    }

    if (liquidPhrase || rmToRoom > 0) {
        story.push(
            <p key="liq">
                {liquidPhrase && <>{hasDownsizeGroup || hasIpGroup ? 'On top of that, ' : ''}{liquidPhrase} {liquidRows.length > 1 ? 'are' : 'is'} paid towards the room.</>}
                {/*
                  * The loan is narrated in full by the block above (EquityReleaseTerms), which names
                  * the product and gives the compounded figure. This clause said a thinner version of
                  * the same thing two paragraphs later; keeping both read as though the plan borrowed
                  * twice. Only the amount going to the room stays, as part of the funding sentence.
                  */}
                {rmToRoom > 0 && <> {formatCurrency(rmToRoom)} of that borrowing goes to the room.</>}
            </p>,
        );
    }
    if (facilityMove) story.push(<p key="move" data-testid="recap-move">{facilityMove}</p>);
    if (villageExit) story.push(<p key="village" data-testid="recap-village">{villageExit}</p>);
    story.push(
        <p key="room">
            {figures.roomFullyPaid ? (
                <>
                    Together that pays the whole {formatCurrency(radInput)} room price up front, so no{' '}
                    <JargonTerm term="DAP" /> is charged on the room. The basic daily fee and the means-tested care
                    fees still apply. The <JargonTerm term="RAD" /> is returned when care ends, less 2% a year for the
                    first five years.
                </>
            ) : (
                <>
                    Together that pays {formatCurrency(Math.min(radPaid, radInput))} of the {formatCurrency(radInput)} room
                    price up front (refunded later, less 2% a year for five years). The unpaid{' '}
                    {formatCurrency(figures.unpaidRoom)} is charged as a <JargonTerm term="DAP" />
                    {dapDaily > 0 ? <> of {formatCurrency(dapDaily)} a day</> : null} — that part works like rent and never
                    comes back.
                </>
            )}
            {/* The second room, where both are in care: said HERE, beside the first room's figure, so
                "$455,000 of the $700,000 room" can never read as the whole plan (owner, 7 Oct 2026). */}
            {secondRoom && secondRoom.roomPrice > 0 && (
                <>
                    {' '}That is {secondRoom.leadName ? `${secondRoom.leadName}'s` : 'the first'} room. {secondRoom.name}&apos;s room is paid the same way:{' '}
                    {secondRoom.radPaid > 0
                        ? <>{formatCurrency(secondRoom.radPaid)} of its {formatCurrency(secondRoom.roomPrice)} price up front
                            {secondRoom.funding && secondRoom.funding.length > 0 ? <> — {fundingPhrase(secondRoom.funding, formatCurrency)}, from what is left after the first deposit{secondRoom.paidFromLabel ? `, taken when ${secondRoom.name} moves in, ${secondRoom.paidFromLabel}` : ''}</> : <>, from what is left after the first deposit</>}
                            ; the unpaid {formatCurrency(Math.max(0, secondRoom.roomPrice - secondRoom.radPaid))} is charged as a <JargonTerm term="DAP" />.</>
                        : <>nothing up front, so its whole {formatCurrency(secondRoom.roomPrice)} price is charged as a <JargonTerm term="DAP" />.</>}
                </>
            )}
            {totalLeftover > 0 && (
                <>
                    {' '}The {formatCurrency(totalLeftover)} left over stays in the bank, where Centrelink counts it and{' '}
                    <JargonTerm term="deems" >
                    {'Centrelink assumes this money earns a set rate of income (deeming) and counts that income in the pension income test, whatever it actually earns.'}
                    </JargonTerm> income on it — this is often what moves the pension.
                </>
            )}
        </p>,
    );

    /*
     * The rental strategy, said out loud. It is two decisions, and both belong in the summary: what
     * the home now earns, and what the partner now pays to live elsewhere. Each carries its own
     * means-test consequence, and they pull in opposite directions on cashflow.
     */
    if (rentsHome && (annualRent > 0 || partnerAnnualRent > 0)) {
        story.push(
            <p key="rent">
                {annualRent > 0 && (
                    <>
                        The family home is <span className="font-semibold text-slate-900">kept and rented out</span> at{' '}
                        {formatCurrency(weeklyRent)} a week — {formatCurrency(annualRent)} a year, less{' '}
                        {formatCurrency(rentExpenses)} of expenses, so{' '}
                        <span className="font-semibold text-slate-900">{formatCurrency(netAnnualRent)}</span> of net rent.
                        That rent is <span className="font-semibold text-slate-900">assessable income</span> for both the
                        Age Pension income test and the aged care means test. The home itself keeps its{' '}
                        <span className="font-semibold text-slate-900">two-year exemption</span> from the pension assets
                        test, and is counted for care fees up to the capped value.
                    </>
                )}
                {partnerAnnualRent > 0 && (
                    <>
                        {annualRent > 0 ? ' ' : ''}
                        {factFind?.partner?.firstName || 'The partner'} pays{' '}
                        <span className="font-semibold text-slate-900">{formatCurrency(partnerWeeklyRent)} a week</span>{' '}
                        ({formatCurrency(partnerAnnualRent)} a year) to rent elsewhere, which comes straight off the
                        household&apos;s cashflow — and may attract Rent Assistance.
                    </>
                )}
            </p>,
        );
    }

    /*
     * EVERY LEVER GETS A SENTENCE. A coverage audit (owner-caught, 2026-08-26: "I'm anxious you don't
     * have explainers for every strategy option") found four that produced NO commentary at all —
     * the Home Equity Access Scheme, equity release generally, clearing debts, and any gifting,
     * funeral bond or annuity chosen OUTSIDE a property sale. A plan can be summarised without
     * mentioning what it actually does, and the client would never know the omission was there.
     */
    const heasLumpSum = Math.max(0, Number(heasDetails?.lumpSum) || 0);
    const heasFortnightly = Math.max(0, Number(heasDetails?.fortnightlyPayment) || 0);
    if (overallStrategy?.heas && (heasLumpSum > 0 || heasFortnightly > 0)) {
        story.push(
            <p key="heas">
                The <span className="font-semibold text-slate-900">Home Equity Access Scheme</span> is used to draw
                {heasLumpSum > 0 ? <> a {formatCurrency(heasLumpSum)} lump sum</> : null}
                {heasLumpSum > 0 && heasFortnightly > 0 ? ' and' : ''}
                {heasFortnightly > 0 ? <> {formatCurrency(heasFortnightly)} a fortnight</> : null} against the home. It is
                a government loan, so nothing is repaid along the way — but interest compounds against the property and
                the debt grows every year it runs, which comes out of the estate at the end.
            </p>,
        );
    }

    const clearedDebts = Object.values(debtPayoffPlan?.payments || {}).reduce((sum, amount) => sum + (Number(amount) || 0), 0);
    if (clearedDebts > 0) {
        story.push(
            <p key="debt">
                <span className="font-semibold text-slate-900">{formatCurrency(clearedDebts)}</span> of debt is cleared.
                The interest stops and the repayments leave the cashflow, and because the money spent is no longer a
                counted asset it can lift the Age Pension too — but it is money the rest of the plan cannot use, which
                is the trade-off worth weighing.
            </p>,
        );
    }

    /*
     * PAYING THE ROOM AS A DAILY FEE is a strategy, and the summary had no sentence for it.
     *
     * Every paragraph above narrates where deposit money came from. When no deposit is paid there is
     * nothing to narrate, and the summary opened straight into the pension and cashflow rows with no
     * account of the plan at all (owner, 2026-09-01). What the client did is keep their capital and
     * rent the room - which has real consequences in both directions, and they belong first.
     */
    // The order the money is used — only where there is a shortfall to fund (owner, 15 Oct 2026).
    if (moneyOrder && cashflowWithPlan != null && cashflowWithPlan < 0) {
        story.push(
            <p key="money-order" data-testid="recap-money-order">
                <span className="font-semibold text-slate-900">Where the shortfall comes from.</span> {moneyOrder}
            </p>,
        );
    }
    if (story.length === 0 && radInput > (Number(radPaid) || 0) + 1) {
        const unpaid = Math.max(0, radInput - (Number(radPaid) || 0));
        story.unshift(
            <p key="dap-route">
                No <JargonTerm term="RAD" /> is paid up front. The{' '}
                <span className="font-semibold text-slate-900">{formatCurrency(unpaid)}</span> of room price left unpaid
                is charged as a <JargonTerm term="DAP" /> — a daily fee, like rent, which does not come back. In exchange
                every dollar stays where it is: available if it is needed, and still earning.
            </p>,
        );
    }

    // Standalone Other Strategies — the ones NOT funded from a property sale, which the sale
    // paragraphs above would otherwise be the only place they were mentioned.
    const standaloneAnnuity = otherStrategies?.annuity && annuityFromSaleProceeds <= 0
        ? Math.max(0, Number(annuityRecord?.purchaseAmount) || 0)
        : 0;
    if (standaloneAnnuity > 0) {
        story.push(
            <p key="annuity">
                <span className="font-semibold text-slate-900">{formatCurrency(standaloneAnnuity)}</span> buys a lifetime
                annuity. Only 60% of the purchase price counts in the assets test (for the first five years, or until age 85 if that is later, and 30% after that), which is what can lift the pension. What it costs depends on the shape:
                with nothing coming back the capital is committed for life, and with capital returned some can be
                taken back, falling each year to nothing.
            </p>,
        );
        story.push(
            <AnnuityTerms
                key="annuity-terms"
                className="mt-1"
                purchaseAmount={standaloneAnnuity}
                residualPercent={Math.round(Math.max(0, Math.min(1, Number(annuityRecord?.residualCapital) || 0)) * 100)}
                ownerName={
                    annuityRecord?.owner === 'partner'
                        ? (factFind?.partner?.firstName || 'your partner')
                        : (factFind?.client?.firstName || 'you')
                }
                partnerName={
                    factFind?.adviceFor === 'couple'
                        ? (annuityRecord?.owner === 'partner'
                            ? (factFind?.client?.firstName || 'you')
                            : (factFind?.partner?.firstName || 'your partner'))
                        : null
                }
                reversionary={!!annuityRecord?.reversionary}
                indexed={!!annuityRecord?.indexation}
                annuityType={String(annuityRecord?.annuityType || 'lifetime')}
                annualPayment={Number(annuityRecord?.annualPayment) || null}
            />,
        );
    } else if (engineAnnuityTerms && Number(engineAnnuityTerms.purchaseAmount) > 0) {
        /*
         * THE ENGINE'S ANNUITY, told as part of the same story.
         *
         * It used to be rendered by the panel AFTER this card, which is how the owner came to see a
         * modal containing one small box and nothing else. Inside the story it reads as one plan
         * with one explanation, which is what "Understanding your plan" is for.
         */
        story.push(
            <p key="annuity-se">
                <span className="font-semibold text-slate-900">
                    {formatCurrency(Number(engineAnnuityTerms.purchaseAmount))}
                </span>{' '}
                buys a lifetime annuity. Only 60% of the purchase price counts in the assets test (for the first five years, or until age 85 if that is later, and 30% after that), and only 60% of the payments count as income — which is
                what lifts the pension and lowers the care fees. What it costs depends on the shape chosen below.
            </p>,
        );
        story.push(
            <EngineAnnuityTerms
                key="annuity-se-terms"
                className="mt-1"
                terms={engineAnnuityTerms}
                factFind={factFind}
                quote={annuityQuoteControl}
            />,
        );

        /*
         * BUYING AN ANNUITY WHILE THE ROOM IS UNPAID IS BORROWING AT THE MPIR TO INVEST BELOW IT.
         *
         * OWNER-RAISED, 2026-09-01: "its using the liquid assets to acquire this annuity which in
         * turn triggers the DAP which is effectively interest at circa 8% so seeing this option in
         * the current market I'm scratching my head."
         *
         * The arithmetic backs the instinct. Every dollar not paid as a deposit is charged the DAP at
         * the MPIR, and none of it comes back; the same dollar in a lifetime annuity earns the
         * annuity rate, which for an older client with full residual capital is far below it. Paying
         * the deposit instead is cheaper on both sides of the means test as well: a RAD is entirely
         * exempt from the Age Pension assets test where an annuity is 60% counted, and it is refunded
         * apart from the 2% a year retention.
         *
         * The engine still models it, and it can still rank well on ESTATE because that retention is
         * a real cost and full residual capital preserves the annuity's value. But a client cannot
         * weigh any of that from a ranking, so the two rates are put side by side here. Shown only
         * when room is actually left unpaid — where the deposit is paid in full there is no borrowing
         * to compare against.
         */
        const annuityRate = Number(engineAnnuityTerms.annualPayment) > 0
            ? (Number(engineAnnuityTerms.annualPayment) / Number(engineAnnuityTerms.purchaseAmount)) * 100
            : null;
        if (radInput > (Number(radPaid) || 0) + 1 && annuityRate != null) {
            story.push(
                <p key="annuity-carry" className="rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white py-2 pl-3 pr-2">
                    <span className="font-semibold text-slate-900">Worth weighing carefully.</span>{' '}
                    Part of the room is left unpaid, and unpaid room is charged at{' '}
                    <span className="font-semibold text-slate-900">{MPIR_PERCENT} a year</span> — money that never comes
                    back. This annuity pays about{' '}
                    <span className="font-semibold text-slate-900">{annuityRate.toFixed(1)}% a year</span>. Putting the
                    same money into the deposit instead would stop the {MPIR_PERCENT} charge, is fully exempt from the
                    Age Pension assets test where only 60% of an annuity is exempt, and is refunded when care ends apart
                    from 2% a year for the first five years.
                </p>,
            );
        }
    }

    /*
     * THE PIECES OF THIS PLAN, in plain words (owner, 24 Sep 2026): one row per piece, what it is
     * and what the means tests do with it, built from the same figures the summary narrates.
     */
    const planPieces: PlanPiece[] = [];
    if (radPaid > 0) planPieces.push({ key: 'rad', name: 'Refundable accommodation deposit (RAD)', amount: radPaid, what: 'Paid up front for the room. It comes back when care ends, less 2% a year for the first five years, and it is not counted in the Age Pension assets test.', glossaryTerm: 'rad' });
    if (secondRoom && secondRoom.roomPrice > 0) {
        planPieces.push({
            key: 'secondRoom',
            name: `${secondRoom.name}'s room`,
            amount: secondRoom.radPaid,
            what: `A room of ${secondRoom.name}'s own, priced at ${formatCurrency(secondRoom.roomPrice)}. ${secondRoom.radPaid > 0 ? `${formatCurrency(secondRoom.radPaid)} is paid up front from what the first deposit leaves; the rest is paid daily.` : 'Nothing is paid up front; the whole room is paid daily.'}${secondRoom.dailyTotal != null ? ` ${secondRoom.name}'s fees come to about ${formatCurrency(secondRoom.dailyTotal)} a day.` : ''}${phaseTwo ? ` ${recapPhaseLine(phaseTwo.names, phaseTwo.firstDaily, phaseTwo.phaseTwoDaily)}` : ''} You are assessed as a couple living apart, each at the single pension rate.`,
            glossaryTerm: 'rad',
        });
    }
    if (figures.unpaidRoom > 0) planPieces.push({ key: 'dap', name: 'Daily accommodation payment (DAP)', amount: figures.dapAnnual, amountSuffix: 'a year', what: `Interest-style rent on the ${formatCurrency(figures.unpaidRoom)} of the room not paid up front, charged daily at the government's set rate. It never comes back.`, glossaryTerm: 'dap' });
    const liquidPaid = liquidRows.reduce((sum, row) => sum + (Number((row as { gross?: number }).gross) || 0), 0);
    if (liquidPaid > 0) planPieces.push({ key: 'savings', name: 'Paid from savings', amount: liquidPaid, what: 'The deposit comes out of bank accounts, term deposits, super pensions or shares. Money that was counted by Centrelink becomes a deposit that is not.', glossaryTerm: 'deeming' });
    if (dzActive && dz.salePrice > 0) planPieces.push({ key: 'sell-home', name: 'Sell the family home', amount: dz.salePrice, what: `The home is sold; after ${formatCurrency(dz.sellingCosts)} of selling costs the money pays for the room and funds what follows. Whatever is not spent on the room or a new home becomes money Centrelink counts.` });
    if (dzActive && dz.newHome > 0) planPieces.push({ key: 'new-home', name: 'Buy a smaller home', amount: dz.newHome, what: 'The new home is the principal residence, so it is exempt from the assets test up to the home exemption cap while it is lived in.', glossaryTerm: 'home exemption cap' });
    if (dzActive && dz.downsizer > 0) planPieces.push({ key: 'downsizer', name: 'Downsizer contribution into super', amount: dz.downsizer, what: 'A special rule lets each person aged 55 or over put up to $300,000 from selling their home into super, on top of the usual limits. Inside super the earnings are taxed at no more than 15%.', glossaryTerm: 'downsizer contribution' });
    if (dzActive && dz.newInvestmentProperty > 0) planPieces.push({ key: 'ip', name: 'Investment property', amount: dz.newInvestmentProperty, what: 'A property bought to rent out. Counted in full as an asset, and the rent after expenses counts as income.', glossaryTerm: 'investment property' });
    if (dzActive && dz.newShares > 0) planPieces.push({ key: 'shares', name: 'Shares / managed funds', amount: dz.newShares, what: 'Invested in a spread of shares and funds you can sell at any time. Counted in full as an asset, and deemed to earn income whatever it actually pays.', glossaryTerm: 'shares and managed funds' });
    if (ipActive && ipNetProceeds > 0) planPieces.push({ key: 'sell-ip', name: 'Sell an investment property', amount: ipNetProceeds, what: 'The property is sold; after selling costs and capital gains tax, the proceeds pay for the room. Its rent stops counting as income.', glossaryTerm: 'capital gains tax' });
    if (ipActive && ip.downsizer > 0) planPieces.push({ key: 'ip-downsizer', name: 'Downsizer contribution into super', amount: ip.downsizer, what: 'Money from the sale moved into super under the downsizer rule, where earnings are taxed at no more than 15%.', glossaryTerm: 'downsizer contribution' });
    if (rentsHome) planPieces.push({ key: 'rent-home', name: 'Keep the home and rent it out', amount: netAnnualRent, amountSuffix: 'a year', what: 'The home is let while care is paid for another way. The rent counts as income; the home stays exempt for two years, then is counted.', glossaryTerm: 'home exemption cap' });
    if (overallStrategy?.reverseMortgage) planPieces.push({ key: 'rm', name: 'Reverse mortgage', amount: rmToRoom > 0 ? rmToRoom : Math.max(0, Number(equityRelease?.loanAmount) || 0), what: 'A loan against the home with no repayments. The interest is added to the loan and the whole amount is repaid from the estate when the home is sold. Not counted as an asset.', glossaryTerm: 'reverse mortgage' });
    if (overallStrategy?.heas && (heasLumpSum > 0 || heasFortnightly > 0)) planPieces.push({ key: 'heas', name: 'Home Equity Access Scheme', amount: heasLumpSum > 0 ? heasLumpSum : heasFortnightly * 26, amountSuffix: heasLumpSum > 0 ? undefined : 'a year', what: 'A government loan against the home, paid as a lump sum or fortnightly, at a lower rate than a bank charges. The debt is repaid from the estate.', glossaryTerm: 'heas' });
    const annuityPurchase = annuityFromSaleProceeds > 0 ? annuityFromSaleProceeds : standaloneAnnuity;
    if (annuityPurchase > 0) {
        const residual = engineAnnuityTerms?.residual != null ? Number(engineAnnuityTerms.residual) : Number(annuityRecord?.residualCapital) || 0;
        const period = engineAnnuityTerms?.capitalAccessPeriodYears != null ? Number(engineAnnuityTerms.capitalAccessPeriodYears) || null : (annuityRecord as { capitalAccessPeriodYears?: number } | null)?.capitalAccessPeriodYears ?? null;
        const sexKnown = engineAnnuityTerms?.lifeExpectancySexKnown != null ? !!engineAnnuityTerms.lifeExpectancySexKnown : ((annuityRecord as { lifeExpectancySexKnown?: boolean } | null)?.lifeExpectancySexKnown ?? null);
        planPieces.push({ key: 'annuity', name: 'Lifetime annuity', amount: annuityPurchase, what: `A lump sum handed to an insurer in return for a guaranteed income for life. Only 60% of it counts in the assets test. ${residual > 0 ? capitalReturnedWords({ residualPct: Math.round(residual * 100), periodYears: period, sexKnown }) : 'Nothing of the purchase price comes back on death.'}`, glossaryTerm: 'lifetime annuity' });
    }
    if (totalGifting > 0) planPieces.push({ key: 'gift', name: 'Gift to family', amount: totalGifting, what: 'Money given away. Up to $10,000 a year ($30,000 over five years) leaves the means tests straight away; anything above that is still counted for five years.', glossaryTerm: 'gifting' });
    const bondTotal = (dzActive ? dz.funeralBond : 0) + (ipActive ? ip.funeralBond : 0);
    if (bondTotal > 0) planPieces.push({ key: 'bond', name: 'Funeral bond', amount: bondTotal, what: 'Money set aside for a funeral with a friendly society or insurer. Within the limit it is exempt from the assets test, and it can only be used for the funeral.', glossaryTerm: 'funeral bond' });
    if (clearedDebtsForWhy > 0) planPieces.push({ key: 'debt', name: 'Debts paid off', amount: clearedDebtsForWhy, what: 'Loans cleared from savings before the room is paid for. Less money is counted, and no more interest is paid.' });

    return (
        <>
        <PlainWordsCard eyebrow={eyebrow} className="mt-4">
            <div className="space-y-1.5 text-sm leading-relaxed text-slate-700">{story}</div>
            {/* EXPANDED rows: this card now opens inside "Understanding your plan", where the whole
                point is the explanation. Hiding each reason behind its own "(why?)" tap wasted the
                room the modal exists to give them (owner, 2026-08-26 — the mock-up showed the whys
                as visible boxes and read far better than three tooltips). */}
            <div className="mt-3 border-t border-slate-100 pt-3">
                <ShiftRow variant="expanded" label="Age Pension" before={pensionDoNothing} after={pensionWithPlan} why={pensionWhy} />
                <ShiftRow variant="expanded" label="Cashflow after care costs" before={cashflowDoNothing} after={cashflowWithPlan} why={cashflowWhy} />
                {/* The estate. Aged care's summary moved pension and cashflow and stopped — leaving
                    the client with no idea what the plan does to what they leave behind, which for
                    many families is the decision (owner-caught, 2026-08-26). */}
                {baselineLegacy != null && (
                    <ShiftRow
                        variant="expanded"
                        label={`Estate after ${legacyYears} ${legacyYears === 1 ? 'year' : 'years'}`}
                        before={baselineLegacy}
                        after={planLegacy ?? baselineLegacy}
                        suffix=""
                        why={legacyWhy}
                    />
                )}
            </div>
            {baselineLegacy != null && planLegacy != null && (
                <p className="mt-2 text-xs leading-relaxed text-slate-600">
                    Projections cannot be guaranteed, but over {legacyYears} {legacyYears === 1 ? 'year' : 'years'} this
                    plan is projected to leave{' '}
                    <span className="font-semibold text-slate-900">{formatCurrency(Math.abs(Math.round(planLegacy - baselineLegacy)))}</span>{' '}
                    {planLegacy >= baselineLegacy ? 'more' : 'less'} to the estate than doing nothing — simply paying the{' '}
                    <JargonTerm term="DAP" /> on the whole room.
                    {planLegacy < baselineLegacy
                        ? ' That is the price of what this plan buys elsewhere: weigh it against the cashflow and pension it delivers.'
                        : ''}
                </p>
            )}
            <p className="mt-1 text-[11px] text-slate-400">
                "With no plan" matches the walk-in note above; "with this plan" matches the Financial Impact panel.
            </p>
            {giftExcess > 0 && (
                <p className="mt-2 rounded-lg border border-slate-200/70 border-l-4 border-l-amber-500 bg-white px-3 py-2 text-sm leading-relaxed text-slate-700 shadow-sm">
                    {formatCurrency(totalGifting)} of the proceeds is gifted — the {formatCurrency(giftExcess)} above the
                    yearly free area still counts for five years under the <JargonTerm term="deprivation" /> rules.
                </p>
            )}
        </PlainWordsCard>
        <PlanPiecesCard className="mt-4" pieces={planPieces} />
        </>
    );
};

export default StrategyPlanRecap;
